/**
 * ChartPickerModal — routes an incoming fax to a client chart.
 *
 * Consumed by:
 *   - ReceivedGrid  (bucket = "Pick a chart" when AI has no confident
 *                    match, or "Change" when overriding a confident one)
 *
 * Wraps `/api/fax-matcher/route-to-intake/{id}` — an atomic backend
 * call that also files the fax into the client's document tray.
 * The modal itself only picks the target chart + document type;
 * follow-up work (rename, tag, etc.) happens in the chart.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Search, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { faxAPI } from "./faxAPI";
import { NAVY, GREEN, NAVY_HAIRLINE } from "../tokens";

const DOC_TYPES = [
  { value: "orders",       label: "Physician Orders" },
  { value: "care_plan",    label: "Care Plan" },
  { value: "authorization",label: "Authorization / PA" },
  { value: "referral",     label: "Referral" },
  { value: "labs",         label: "Labs / Imaging" },
  { value: "correspondence", label: "Correspondence" },
  { value: "other",        label: "Other" },
];

export default function ChartPickerModal({ fax, onClose, onFiled }) {
  const [options, setOptions] = useState(null);
  const [loadingOpts, setLoadingOpts] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [docType, setDocType] = useState("orders");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!fax) return;
    (async () => {
      try {
        const r = await faxAPI.routeOptions(fax.id);
        if (!alive) return;
        setOptions(r);
        // If backend already surfaced a suggested top-candidate, seed selection.
        if (r.suggested?.patient_id) setSelectedId(r.suggested.patient_id);
        if (r.suggested?.doc_type) setDocType(r.suggested.doc_type);
      } catch (e) {
        toast.error(`Could not load candidates: ${e.message}`);
      } finally {
        if (alive) setLoadingOpts(false);
      }
    })();
    return () => { alive = false; };
  }, [fax]);

  const patients = options?.candidates || [];
  const filtered = useMemo(() => {
    if (!q.trim()) return patients;
    const term = q.trim().toLowerCase();
    return patients.filter(p => {
      const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
      return name.includes(term);
    });
  }, [patients, q]);

  const submit = async () => {
    if (!selectedId) return toast.error("Pick a client first");
    setSaving(true);
    try {
      await faxAPI.routeToIntake(fax.id, {
        patient_id: selectedId,
        doc_type: docType,
      });
      toast.success("Fax filed to chart");
      onFiled && onFiled({ patient_id: selectedId, doc_type: docType });
      onClose();
    } catch (e) {
      toast.error(e.message || "Assign failed");
    } finally { setSaving(false); }
  };

  if (!fax) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(20,56,117,0.35)" }} data-testid="comms2-fax-chart-picker">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col" style={{ border: `1px solid ${NAVY_HAIRLINE}` }}>
        <div className="shrink-0 flex items-center justify-between px-4 h-12 border-b" style={{ borderColor: NAVY_HAIRLINE }}>
          <div className="text-[14px] font-semibold" style={{ color: NAVY }}>Pick a chart for this fax</div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center" data-testid="comms2-fax-chart-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Search */}
        <div className="shrink-0 px-4 py-2 border-b" style={{ borderColor: NAVY_HAIRLINE }}>
          <div className="flex items-center gap-2 h-9 px-3 rounded-md border" style={{ borderColor: NAVY_HAIRLINE }}>
            <Search className="w-4 h-4 opacity-60" style={{ color: NAVY }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients"
              className="flex-1 text-[13px] bg-transparent outline-none"
              autoFocus
              data-testid="comms2-fax-chart-search"
            />
          </div>
        </div>
        {/* Candidate list */}
        <div className="flex-1 overflow-y-auto">
          {loadingOpts && (
            <div className="py-8 text-center text-slate-400 text-[13px]"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading candidates…</div>
          )}
          {!loadingOpts && filtered.length === 0 && (
            <div className="py-8 text-center text-slate-400 text-[13px]">No matching clients</div>
          )}
          {filtered.map(p => (
            <button
              key={p.patient_id || p.id}
              onClick={() => setSelectedId(p.patient_id || p.id)}
              className="w-full text-left px-4 py-2.5 border-b hover:bg-slate-50 flex items-center gap-3"
              style={{ borderColor: NAVY_HAIRLINE, background: (selectedId === (p.patient_id || p.id)) ? "rgba(20,56,117,0.06)" : "transparent" }}
              data-testid={`comms2-fax-chart-candidate-${p.patient_id || p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate" style={{ color: NAVY }}>{p.first_name} {p.last_name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {[p.mrn && `MRN ${p.mrn}`, p.dob && `DOB ${p.dob}`, p.match_reason].filter(Boolean).join(" · ")}
                </div>
              </div>
              {typeof p.confidence === "number" && (
                <span className="text-[10.5px] font-semibold tabular-nums" style={{ color: NAVY }}>
                  {Math.round(p.confidence * 100)}%
                </span>
              )}
              {selectedId === (p.patient_id || p.id) && <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} />}
            </button>
          ))}
        </div>
        {/* Doc type + submit */}
        <div className="shrink-0 p-3 border-t flex items-center gap-2" style={{ borderColor: NAVY_HAIRLINE }}>
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="h-9 rounded-md border text-[12.5px] px-2 bg-white"
            style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
            data-testid="comms2-fax-chart-doctype"
          >
            {DOC_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
          </select>
          <div className="flex-1" />
          <button onClick={onClose} className="h-9 px-3 rounded-md text-[12.5px] font-medium hover:bg-slate-100" data-testid="comms2-fax-chart-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!selectedId || saving}
            className="h-9 px-4 rounded-md text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-40"
            style={{ background: GREEN }}
            data-testid="comms2-fax-chart-submit"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            File to chart
          </button>
        </div>
      </div>
    </div>
  );
}
